import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BarChart3, TrendingUp, Users, Bookmark, Share2, Mail, Newspaper, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const { getAuthHeader } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: getAuthHeader()
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FF6B6B] border-t-transparent"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center">
        <p className="text-xl text-gray-600">No analytics data available</p>
      </div>
    );
  }

  const { overview, category_stats, top_news } = analytics;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border-2 border-black rounded-xl p-6 hard-shadow-lg">
      <div className="flex items-center gap-3 mb-3">
        <div className={`${color} p-2 rounded-lg border-2 border-black`}>
          <Icon className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <span className="text-sm font-bold text-gray-600 uppercase">{label}</span>
      </div>
      <p className="text-4xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
        {value}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFDF5]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-10 h-10 text-[#FF6B6B]" strokeWidth={2.5} />
            <h1 className="text-5xl md:text-7xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              Analytics
            </h1>
          </div>
          <p className="text-xl text-gray-600">
            Track your platform's performance and user engagement
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
            Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Newspaper}
              label="Total News"
              value={overview.total_news}
              color="bg-[#FF6B6B] text-white"
            />
            <StatCard
              icon={Users}
              label="Users"
              value={overview.total_users}
              color="bg-[#4ECDC4] text-black"
            />
            <StatCard
              icon={Bookmark}
              label="Bookmarks"
              value={overview.total_bookmarks}
              color="bg-[#FFE66D] text-black"
            />
            <StatCard
              icon={Share2}
              label="Shares"
              value={overview.total_shares}
              color="bg-[#95E1D3] text-black"
            />
            <StatCard
              icon={Eye}
              label="Views"
              value={overview.total_views}
              color="bg-[#F38181] text-white"
            />
            <StatCard
              icon={Mail}
              label="Newsletter"
              value={overview.total_newsletter_subscribers}
              color="bg-[#2D2A26] text-white"
            />
            <StatCard
              icon={TrendingUp}
              label="Humor Level"
              value={overview.current_humor_level}
              color="bg-white text-black"
            />
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
            Category Performance
          </h2>
          <div className="bg-white border-2 border-black rounded-xl p-6 hard-shadow-lg overflow-x-auto">
            <table className="w-full" data-testid="category-stats-table">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-3 px-4 font-bold uppercase text-sm">Category</th>
                  <th className="text-center py-3 px-4 font-bold uppercase text-sm">News</th>
                  <th className="text-center py-3 px-4 font-bold uppercase text-sm">Views</th>
                  <th className="text-center py-3 px-4 font-bold uppercase text-sm">Bookmarks</th>
                  <th className="text-center py-3 px-4 font-bold uppercase text-sm">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {category_stats.map((cat, index) => (
                  <tr
                    key={cat.category}
                    className={index !== category_stats.length - 1 ? 'border-b border-gray-200' : ''}
                    data-testid={`category-row-${cat.category}`}
                  >
                    <td className="py-3 px-4 font-bold capitalize">{cat.category}</td>
                    <td className="text-center py-3 px-4">{cat.news_count}</td>
                    <td className="text-center py-3 px-4">{cat.views}</td>
                    <td className="text-center py-3 px-4">{cat.bookmarks}</td>
                    <td className="text-center py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <div
                          className="bg-[#FF6B6B] h-2 rounded-full"
                          style={{
                            width: `${Math.min((cat.views + cat.bookmarks) / 10, 100)}px`
                          }}
                        ></div>
                        <span className="text-sm font-bold">{cat.views + cat.bookmarks}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
            Top Performing News
          </h2>
          <div className="space-y-4">
            {top_news.slice(0, 10).map((news, index) => (
              <div
                key={news.id}
                data-testid={`top-news-${index}`}
                className="bg-white border-2 border-black rounded-xl p-6 hard-shadow hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-[#FFE66D] px-3 py-1 rounded-full border-2 border-black text-xs font-bold uppercase">
                        #{index + 1}
                      </span>
                      <span className="bg-[#4ECDC4] px-3 py-1 rounded-full border-2 border-black text-xs font-bold uppercase">
                        {news.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                      {news.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Bookmark className="w-4 h-4" strokeWidth={2.5} />
                      <span className="font-bold">{news.bookmark_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Share2 className="w-4 h-4" strokeWidth={2.5} />
                      <span className="font-bold">{news.share_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
